import { useMemo, useState } from 'react'
import { useStore } from '../lib/store'
import { signalCounts } from '../lib/candidate'
import { canActOnPipeline } from '../lib/permissions'
import type { Candidate } from '../lib/types'
import { Avatar, Badge, Button, Card } from '../components/ui'
import { CredentialBadge } from '../components/Verification'
import { GhostMark } from '../components/GhostMark'
import { IconCheck, IconSend, IconShield } from '../components/icons'
import './pages.css'

function statusBadge(c: Candidate) {
  switch (c.verifyRequest) {
    case 'pending':
      return <Badge color="var(--c-warn)" bg="var(--c-warn-bg)" dot>Awaiting candidate</Badge>
    case 'completed':
      return <Badge color="var(--cred-live)" bg="var(--cred-live-bg)" dot>Completed</Badge>
    case 'declined':
      return <Badge color="var(--c-error)" bg="var(--c-error-bg)">Declined</Badge>
    default:
      return null
  }
}

export function SendVerification() {
  const { candidates, sendVerifyRequests, session } = useStore()
  const canAct = canActOnPipeline(session.recruiter.seatRole)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [sending, setSending] = useState(false)
  const [sentSummary, setSentSummary] = useState<{ sent: number; emailed: number } | null>(null)
  const [error, setError] = useState('')

  // Candidates who can be asked to self-verify: not already pending/completed.
  const eligible = useMemo(
    () => candidates.filter((c) => c.verifyRequest === 'none' || c.verifyRequest === 'declined'),
    [candidates],
  )
  const pending = useMemo(() => candidates.filter((c) => c.verifyRequest === 'pending'), [candidates])

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function send() {
    const ids = [...selected]
    if (!ids.length || sending) return
    setSending(true)
    setError('')
    try {
      const summary = await sendVerifyRequests(ids)
      setSentSummary(summary)
      setSelected(new Set())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the requests. Try again.')
    } finally {
      setSending(false)
    }
  }

  const allSelected = eligible.length > 0 && eligible.every((c) => selected.has(c.id))

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Send verification requests</h1>
          <p className="page__sub">
            Ask candidates to complete the next verification step themselves. They confirm on their own device — you never
            handle their documents.
          </p>
        </div>
      </div>

      <div className="verify-layout">
        <div>
          {eligible.length > 0 && (
            <div className="row-between" style={{ marginBottom: '0.85rem' }}>
              <div className="section-title" style={{ margin: 0 }}>
                {eligible.length} candidate{eligible.length === 1 ? '' : 's'} ready to advance
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelected(allSelected ? new Set() : new Set(eligible.map((c) => c.id)))}
              >
                {allSelected ? 'Clear all' : 'Select all'}
              </Button>
            </div>
          )}

          {eligible.map((c) => {
            const isSel = selected.has(c.id)
            const { present, total } = signalCounts(c)
            return (
              <div
                key={c.id}
                className={`vrow${isSel ? ' is-selected' : ''}`}
                onClick={() => toggle(c.id)}
                role="checkbox"
                aria-checked={isSel}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    toggle(c.id)
                  }
                }}
              >
                <span className="vrow__check">
                  <IconCheck size={14} />
                </span>
                <Avatar hue={c.avatarHue} initials={c.initials} size={38} />
                <div className="vrow__id">
                  <div className="vrow__name">{c.name}</div>
                  <div className="vrow__sub">
                    {c.headline} · {present}/{total} signals
                  </div>
                </div>
                <GhostMark size={22} tint={c.state} breathing={c.state === 'live'} />
              </div>
            )
          })}

          {pending.length > 0 && (
            <>
              <div className="section-title" style={{ marginTop: '1.75rem' }}>
                Awaiting response ({pending.length})
              </div>
              {pending.map((c) => (
                <div key={c.id} className="vrow is-disabled" aria-disabled>
                  <Avatar hue={c.avatarHue} initials={c.initials} size={38} />
                  <div className="vrow__id">
                    <div className="vrow__name">{c.name}</div>
                    <div className="vrow__sub">{c.headline}</div>
                  </div>
                  {statusBadge(c)}
                </div>
              ))}
            </>
          )}

          {eligible.length === 0 && pending.length === 0 && (
            <Card>
              <p className="muted">Every candidate in your pool already has a request out or completed. Nice work.</p>
            </Card>
          )}
        </div>

        <Card className="verify-summary">
          <div className="section-title">Request</div>
          <div className="summary-count tnum">{selected.size}</div>
          <p className="rung__blurb" style={{ marginBottom: '1rem' }}>
            candidate{selected.size === 1 ? '' : 's'} selected. Each gets a secure, single-use link to self-verify — it
            never exposes their personal data to you.
          </p>

          <Button
            variant="primary"
            block
            icon={<IconSend size={16} />}
            loading={sending}
            disabled={selected.size === 0 || !canAct || sending}
            title={!canAct ? 'Viewers have read-only access' : undefined}
            onClick={send}
          >
            Send {selected.size > 0 ? `${selected.size} ` : ''}request{selected.size === 1 ? '' : 's'}
          </Button>
          {!canAct && (
            <p className="rung__blurb" style={{ marginTop: '0.6rem', textAlign: 'center' }}>
              Viewers have read-only access — ask a recruiter or admin to send requests.
            </p>
          )}

          {error && (
            <div
              className="toolbar__note"
              style={{ marginTop: '1rem', marginBottom: 0, color: 'var(--c-error)' }}
            >
              {error}
            </div>
          )}

          {sentSummary && sentSummary.sent > 0 && (
            <div
              className="toolbar__note"
              style={{ marginTop: '1rem', marginBottom: 0, color: 'var(--cred-live)' }}
            >
              <IconCheck size={14} /> Sent {sentSummary.sent} request{sentSummary.sent === 1 ? '' : 's'}
              {sentSummary.emailed > 0
                ? ` — ${sentSummary.emailed} emailed, tracking in Activity.`
                : ' — tracking in Activity.'}
            </div>
          )}

          <hr className="divider" />
          <div className="kv" style={{ gap: '0.6rem' }}>
            <div className="setting-row__sub" style={{ display: 'flex', gap: '0.45rem', alignItems: 'flex-start' }}>
              <span style={{ color: 'var(--cred-live)', flex: 'none', marginTop: 2, display: 'flex' }}>
                <IconShield size={14} />
              </span>
              Candidates verify on their own device. You receive the result, never the raw documents.
            </div>
            <div className="setting-row__sub" style={{ display: 'flex', gap: '0.45rem', alignItems: 'flex-start' }}>
              <CredentialBadge state="vault" />
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}
